import { ExternalLink, BookOpen, HelpCircle, FileText, Cloud, Phone, Key, Activity, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QuickLink {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  category: 'docs' | 'external' | 'system';
  status?: 'online' | 'warning' | 'offline';
}

export function LinksWidget() {
  const links: QuickLink[] = [
    // Documentation
    {
      title: 'API Skjölun',
      description: 'Tæknileg skjölun fyrir Prima API',
      href: 'https://github.com/2076ehf/prima.myx.is/blob/main/docs/API.md',
      icon: <BookOpen className="w-4 h-4" />,
      category: 'docs',
    },
    {
      title: 'Hjálp & Stuðningur',
      description: 'Leiðbeiningar og algengar spurningar',
      href: 'https://github.com/2076ehf/prima.myx.is/blob/main/README.md',
      icon: <HelpCircle className="w-4 h-4" />,
      category: 'docs',
    },
    {
      title: 'Breytingaskrá',
      description: 'Nýjustu uppfærslur og eiginleikar',
      href: 'https://github.com/2076ehf/prima.myx.is/commits/main',
      icon: <FileText className="w-4 h-4" />,
      category: 'docs',
    },

    // External Services
    {
      title: 'Cloudflare Dashboard',
      description: 'Workers, D1, R2 og KV stillingar',
      href: 'https://dash.cloudflare.com',
      icon: <Cloud className="w-4 h-4" />,
      category: 'external',
      status: 'online',
    },
    {
      title: 'Twilio Console',
      description: 'Símanúmer og símtalstenging',
      href: 'https://console.twilio.com',
      icon: <Phone className="w-4 h-4" />,
      category: 'external',
      status: 'online',
    },
    {
      title: 'Google Cloud Console',
      description: 'Dialogflow CX og Speech API',
      href: 'https://console.cloud.google.com',
      icon: <Key className="w-4 h-4" />,
      category: 'external',
      status: 'online',
    },

    // System Status
    {
      title: 'Kerfisstaða',
      description: 'Rauntímaeftirlit með þjónustum',
      href: 'https://www.cloudflarestatus.com',
      icon: <Activity className="w-4 h-4" />,
      category: 'system',
      status: 'online',
    },
  ];

  const getCategoryTitle = (category: string) => {
    switch (category) {
      case 'docs': return 'Skjöl & Hjálp';
      case 'external': return 'Ytri þjónustur';
      case 'system': return 'Kerfi';
      default: return '';
    }
  };

  const groupedLinks = links.reduce((acc, link) => {
    if (!acc[link.category]) acc[link.category] = [];
    acc[link.category].push(link);
    return acc;
  }, {} as Record<string, QuickLink[]>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 p-6 text-white">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />

        <div className="relative">
          <h2 className="text-2xl font-bold mb-2">Hraðtenglar</h2>
          <p className="text-blue-100 text-sm">
            Aðgangur að skjölum, ytri þjónustum og kerfisupplýsingum
          </p>
        </div>
      </div>

      {/* Links grouped by category */}
      {Object.entries(groupedLinks).map(([category, categoryLinks]) => (
        <div key={category} className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900 px-1">
            {getCategoryTitle(category)}
          </h3>

          <div className="space-y-2">
            {categoryLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "group relative overflow-hidden rounded-lg border transition-all duration-300",
                  "hover:shadow-md hover:scale-[1.02] hover:border-blue-300",
                  "bg-white/80 backdrop-blur-sm border-gray-200/50"
                )}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative p-4">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={cn(
                      "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0",
                      "bg-gradient-to-br from-blue-500 to-purple-500 text-white",
                      "group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-md"
                    )}>
                      {link.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                          {link.title}
                        </h4>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {link.status && (
                            <div className="flex items-center gap-1">
                              <div className={cn(
                                "w-2 h-2 rounded-full",
                                link.status === 'online' && "bg-green-500 shadow-lg shadow-green-500/50 animate-pulse",
                                link.status === 'warning' && "bg-yellow-500 shadow-lg shadow-yellow-500/50",
                                link.status === 'offline' && "bg-red-500 shadow-lg shadow-red-500/50"
                              )} />
                            </div>
                          )}

                          <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                      </div>

                      <p className="text-xs text-gray-600 leading-relaxed">
                        {link.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Hover accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </a>
            ))}
          </div>
        </div>
      ))}

      {/* System Health Summary */}
      <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200/50 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/30">
            <CheckCircle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-green-900 mb-0.5">
              Allar þjónustur virkar
            </h4>
            <p className="text-xs text-green-700">
              Síðast uppfært: {new Date().toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
      </div>

      {/* Footer note */}
      <div className="text-center pt-2">
        <p className="text-xs text-gray-500">
          Tenglar opnast í nýjum glugga • Öruggur aðgangur með OAuth
        </p>
      </div>
    </div>
  );
}
